import React, { useState, useMemo } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { CanCreate, CanEdit, CanDelete } from "@/shared/components/PermissionGate";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Eye,
  Power,
  PowerOff,
  Trash2,
  Zap,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  TrendingUp,
  Building2,
} from "lucide-react";

export default function AdminEngines() {
  const [search, setSearch] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();

  const statusConfig = useMemo(() => ({
    ACTIVE: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("active") },
    SUSPENDED: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("suspended") },
    INACTIVE: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("inactive") },
  }), [t, language]);

  const { data: engines = [], isLoading } = useQuery({
    queryKey: ["admin-engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateEngine(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-engines"]);
      setStatusDialog(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteEngine(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-engines"]);
      setDeleteDialog(null);
    },
  });

  const filteredEngines = engines.filter(e =>
    e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleStatus = async () => {
    if (!statusDialog) return;
    const newStatus = statusDialog.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({ id: statusDialog.id, data: { name: statusDialog.name, status: newStatus } });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("engines")}</h1>
          <p className="text-slate-500">{engines.length} {t("enginePartners")}</p>
        </div>
        <CanCreate resource="engines">
          <Link to={createPageUrl("AdminEngineForm")}>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              {t("createEngine")}
            </Button>
          </Link>
        </CanCreate>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder={t("searchEngines")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">{t("engine")}</TableHead>
              <TableHead className="font-semibold hidden sm:table-cell">{t("code")}</TableHead>
              <TableHead className="font-semibold">{t("status")}</TableHead>
              <TableHead className="font-semibold hidden md:table-cell">{t("resellers")}</TableHead>
              <TableHead className="font-semibold hidden md:table-cell">{t("merchants")}</TableHead>
              <TableHead className="font-semibold hidden lg:table-cell">{t("volume")}</TableHead>
              <TableHead className="font-semibold hidden lg:table-cell">{t("created")}</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500">{t("loading")}</TableCell></TableRow>
            ) : filteredEngines.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500">{t("engines")}</TableCell></TableRow>
            ) : (
              filteredEngines.map((engine, i) => {
                const status = statusConfig[engine.status] || statusConfig.INACTIVE;
                const StatusIcon = status.icon;
                return (
                  <motion.tr
                    key={engine.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.02 }}
                    className="hover:bg-slate-50"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                          <Zap className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="font-medium">{engine.name}</p>
                          <p className="text-sm text-slate-500">{engine.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="outline">{engine.id.substring(0, 8)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${status.color} gap-1 border-0`}>
                        <StatusIcon className="h-3 w-3" />
                        <span className="hidden sm:inline">{status.label}</span>
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-slate-400" />
                        <span>{resellers.filter(r => r.createdByEngineId === engine.id).length}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-1">
                        <Building2 className="h-4 w-4 text-slate-400" />
                        <span>{merchants.filter(m => m.createdByEngineId === engine.id).length}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-4 w-4 text-emerald-500" />
                        <span className="font-semibold">฿{(Math.floor(Math.random() * 50000000) + 1000000).toLocaleString()}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500 hidden lg:table-cell">
                      {engine.createdAt ? format(new Date(engine.createdAt), "MMM d, yyyy") : "N/A"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={createPageUrl("AdminEngineDetail") + `?id=${engine.id}`}>
                              <Eye className="h-4 w-4 mr-2" />{t("view")}
                            </Link>
                          </DropdownMenuItem>
                          <CanEdit resource="engines">
                            <DropdownMenuItem asChild>
                              <Link to={createPageUrl("AdminEngineForm") + `?id=${engine.id}`}>
                                <Pencil className="h-4 w-4 mr-2" />{t("edit")}
                              </Link>
                            </DropdownMenuItem>
                          </CanEdit>
                          <CanEdit resource="engines">
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setStatusDialog(engine)}>
                              {engine.status === "ACTIVE" ? (
                                <><PowerOff className="h-4 w-4 mr-2" />{t("suspend")}</>
                              ) : (
                                <><Power className="h-4 w-4 mr-2" />{t("activate")}</>
                              )}
                            </DropdownMenuItem>
                          </CanEdit>
                          <CanDelete resource="engines">
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setDeleteDialog(engine)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" />{t("delete")}
                            </DropdownMenuItem>
                          </CanDelete>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </motion.tr>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{statusDialog?.status === "ACTIVE" ? t("suspendEngine") : t("activateEngine")}</AlertDialogTitle>
            <AlertDialogDescription>
              {statusDialog?.status === "ACTIVE"
                ? `${t("suspend")} "${statusDialog?.name}"? ${t("allResellersAffected")}`
                : `${t("activate")} "${statusDialog?.name}"?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleStatus} className={statusDialog?.status === "ACTIVE" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}>
              {statusDialog?.status === "ACTIVE" ? t("suspend") : t("activate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteEngine")}</AlertDialogTitle>
            <AlertDialogDescription>{t("deleteConfirmation")} "{deleteDialog?.name}"? {t("cannotBeUndone")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMutation.mutate(deleteDialog.id)} className="bg-red-600 hover:bg-red-700">{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}